import { useState, useEffect, useRef } from "react"
import { api } from "../../../lib/api/client"
import {
  useObjectCacheStore,
  type CachedObject,
} from "../../../stores/object-cache-store"

// Convert camelCase API client keys to snake_case Mongoose model names
// e.g. "textTemplates" → "text_templates", "emailTemplates" → "email_templates"
function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

// Module-level batch queue - shared across all ObjectName instances
let batchQueue: Record<string, string[]> = {}
let batchTimeout: ReturnType<typeof setTimeout> | null = null
let batchTries = 0

/**
 * Process the batch queue - fetches all queued objects in a single API call
 */
async function processBatchQueue() {
  batchTries++
  if (batchTries > 5) {
    batchTries = 0
    return
  }

  const collections = { ...batchQueue }
  batchQueue = {}

  const keys = Object.keys(collections)
  if (keys.length === 0) return

  // Convert camelCase collection names to snake_case for the server batch endpoint
  // (Mongoose models are registered as snake_case, e.g. "text_templates" not "textTemplates")
  const snakeCollections: Record<string, string[]> = {}
  const snakeToOriginal: Record<string, string> = {}
  for (const key of keys) {
    const snakeKey = camelToSnake(key)
    snakeCollections[snakeKey] = collections[key]
    snakeToOriginal[snakeKey] = key
  }

  try {
    const response = await api.query.batch(snakeCollections)
    if (response.data) {
      // Filter out empty collections to avoid continuous polling
      const filteredData: Record<string, CachedObject[]> = {}
      Object.entries(response.data).forEach(([snakeKey, objects]) => {
        if (objects && objects.length > 0) {
          // Map snake_case response key back to original collection name for cache
          const originalKey = snakeToOriginal[snakeKey] || snakeKey
          filteredData[originalKey] = objects
        }
      })

      if (Object.keys(filteredData).length > 0) {
        useObjectCacheStore.getState().setMultipleCollections(filteredData)
      }
    }
    batchTries = 0
  } catch (error) {
    console.error("Error fetching batch objects:", error)
  }
}

/**
 * Add an object to the batch queue
 */
function queueForBatch(collection: string, id: string) {
  if (!batchQueue[collection]) {
    batchQueue[collection] = []
  }

  if (!batchQueue[collection].includes(id)) {
    batchQueue[collection].push(id)
  }

  // Clear existing timeout and set a new one
  if (batchTimeout) {
    clearTimeout(batchTimeout)
  }

  batchTimeout = setTimeout(processBatchQueue, 100)
}

export interface UseObjectResolverResult {
  object: CachedObject | null
  isLoading: boolean
  isError: boolean
}

/**
 * Hook to resolve an object ID to its full object data.
 * Uses a shared batch queue to efficiently fetch multiple objects in a single API call.
 *
 * @param collection - The MongoDB collection name
 * @param id - The object ID to resolve
 * @returns Object with the resolved object, loading state, and error state
 */
export function useObjectResolver(
  collection: string,
  id: string
): UseObjectResolverResult {
  const [object, setObject] = useState<CachedObject | null>(null)
  const [isError, setIsError] = useState(false)
  const hasQueuedRef = useRef(false)

  // Get store methods
  const getObject = useObjectCacheStore((state) => state.getObject)
  const cache = useObjectCacheStore((state) => state.cache)

  // Check cache and queue for fetch if needed
  useEffect(() => {
    if (!id || !collection) {
      setObject(null)
      return
    }

    // Check cache first
    const cached = getObject(collection, id)
    if (cached) {
      setObject(cached)
      setIsError(false)
      hasQueuedRef.current = false
      return
    }

    // Queue for batch fetch if not already queued
    if (!hasQueuedRef.current) {
      hasQueuedRef.current = true
      queueForBatch(collection, id)
    }
  }, [collection, id, getObject])

  // Subscribe to cache changes for this specific object
  useEffect(() => {
    if (!id || !collection) return

    const cached = cache[collection]?.[id]
    if (cached && !object) {
      setObject(cached)
      setIsError(false)
    }
  }, [cache, collection, id, object])

  // Set error state after timeout if object not found
  useEffect(() => {
    if (object) {
      setIsError(false)
      return
    }

    const errorTimeout = setTimeout(() => {
      if (!object) {
        setIsError(true)
      }
    }, 3000)

    return () => clearTimeout(errorTimeout)
  }, [object, collection, id])

  // Reset queued flag when collection or id changes
  useEffect(() => {
    hasQueuedRef.current = false
  }, [collection, id])

  return {
    object,
    isLoading: !object && !isError,
    isError,
  }
}
